import { useMemo, useState } from 'react';
import { Gear } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import { useT } from '@/i18n/LanguageContext';

export const EQUIPMENT_STATUS_STYLE = {
  operational: 'bg-emerald-50 text-emerald-700',
  maintenance: 'bg-amber-50 text-amber-700',
  breakdown: 'bg-rose-50 text-rose-600',
  retired: 'bg-zinc-100 text-zinc-500',
};

// 設備検索パレット — BOM の使用設備を検索して追加する
export default function EquipmentPickerDialog({ open, onOpenChange, equipment, excludeIds = [], onPick }) {
  const { t } = useT();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const results = useMemo(() => equipment
    .filter((e) => !excludeIds.includes(e.id))
    .filter((e) => !q || [e.name, e.asset_no, e.maker, e.model, e.serial, e.kind_label]
      .some((v) => String(v || '').toLowerCase().includes(q))), [equipment, excludeIds, q]);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setQuery(''); onOpenChange(o); }}>
      <DialogContent className="max-w-xl p-0 gap-0 overflow-hidden" aria-describedby={undefined} data-testid="bom-equipment-modal">
        <DialogTitle className="sr-only">{t('mfg.bom.equipmentSearchTitle')}</DialogTitle>
        <Command shouldFilter={false} className="rounded-none">
          <CommandInput autoFocus value={query} onValueChange={setQuery}
            placeholder={t('mfg.bom.equipmentSearchPh')} data-testid="bom-equipment-search" />
          <div className="flex items-center justify-end border-b border-zinc-200 bg-zinc-50/60 px-3 py-1.5">
            <span className="text-[10px] text-zinc-400" data-testid="bom-equipment-count">
              {t('mfg.itemPicker.countHint', { count: results.length })}
            </span>
          </div>
          <CommandList className="max-h-[60vh]">
            {results.length === 0 && <CommandEmpty data-testid="bom-equipment-no-match">{t('mfg.bom.equipmentNoMatch')}</CommandEmpty>}
            {results.map((e) => (
              <CommandItem key={e.id} value={e.id} onSelect={() => { onPick(e); setQuery(''); onOpenChange(false); }}
                data-testid={`bom-equipment-opt-${e.id}`} className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm text-zinc-800">
                    <Gear size={13} weight="duotone" className="text-sky-500 shrink-0" />
                    <span className="truncate">{e.name}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1">
                    {e.asset_no && <span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] text-zinc-500">{e.asset_no}</span>}
                    {(e.maker || e.model) && <span className="text-[10px] text-zinc-400">{[e.maker, e.model].filter(Boolean).join(' ')}</span>}
                    {e.kind_label && <span className="text-[10px] text-zinc-300">{e.kind_label}</span>}
                  </div>
                </div>
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${EQUIPMENT_STATUS_STYLE[e.status] || 'bg-zinc-100 text-zinc-500'}`}>
                  {e.status_label || e.status || ''}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
